import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatErr } from "@/lib/format";
import { MfaChallenge } from "@/components/MfaChallenge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Check } from "lucide-react";

export default function Signup() {
    const { signup } = useAuth();
    const navigate = useNavigate();
    const [f, setF] = useState({
        school_name: "", school_route: "", admin_name: "", admin_email: "", password: "",
        phone: "", contact_email: "", address_line1: "", address_line2: "", city: "", state: "", zip_code: "",
    });
    const [busy, setBusy] = useState(false);
    const [challenge, setChallenge] = useState(null);
    const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!f.school_name || !f.school_route || !f.admin_name || !f.admin_email || !f.password) {
            toast.error("Please complete the required fields"); return;
        }
        if (f.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
        setBusy(true);
        try {
            const res = await signup(f);
            if (res.mfa) setChallenge(res);
            else { toast.success("Welcome to ProcureEd — your 30-day trial has started"); navigate("/app"); }
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const F = ({ k, label, req, ...p }) => (
        <div>
            <Label className="text-xs">{label}{req && <span className="text-rose-500"> *</span>}</Label>
            <Input value={f[k]} onChange={(e) => set(k, e.target.value)} className="mt-1" data-testid={`signup-${k}`} {...p} />
        </div>
    );

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            <div className="hidden lg:flex flex-col justify-between bg-emerald-50 text-brand-emerald p-12 relative overflow-hidden">
                <div className="relative z-10"><img src="/procureed-logo.png" alt="ProcureEd" className="h-12 w-auto" /></div>
                <div className="relative z-10 max-w-md">
                    <h2 className="text-3xl font-semibold tracking-tight leading-tight">Start your school's 30-day free trial</h2>
                    <ul className="mt-6 space-y-2 text-slate-600">
                        {["Full purchasing workflow", "AI invoice scanning & 3-way match", "Veracross AP posting", "No credit card to start"].map((t) => (
                            <li key={t} className="flex items-center gap-2"><Check className="h-4 w-4 text-brand-teal" /> {t}</li>
                        ))}
                    </ul>
                </div>
                <div className="relative z-10 flex items-center gap-3 text-sm">
                    <img src="/veracross-logo.png" alt="Veracross" className="h-6 w-auto" />
                    <span className="text-muted-foreground">Veracross Integration Partner</span>
                </div>
                <div className="absolute -right-20 -bottom-20 h-80 w-80 rounded-full bg-brand-teal/10 blur-3xl" />
            </div>

            <div className="flex flex-col justify-center px-6 sm:px-12 py-10 bg-brand-bone overflow-y-auto">
                <div className="w-full max-w-lg mx-auto">
                    <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-brand-emerald mb-6" data-testid="back-home-link">
                        <ArrowLeft className="h-4 w-4" /> Back to home
                    </Link>
                    <div className="lg:hidden mb-6"><img src="/procureed-logo.png" alt="ProcureEd" className="h-11 w-auto" /></div>

                    {challenge ? (
                        <>
                            <h1 className="text-2xl font-semibold tracking-tight text-brand-emerald mb-1">Secure your account</h1>
                            <p className="text-sm text-muted-foreground mb-4">As a School Admin you must enable 2-step verification.</p>
                            <MfaChallenge challenge={challenge} onComplete={() => { toast.success("Trial started — welcome to ProcureEd"); navigate("/app"); }} />
                        </>
                    ) : (
                        <>
                            <h1 className="text-2xl font-semibold tracking-tight text-brand-emerald">Create your school account</h1>
                            <p className="text-sm text-muted-foreground mt-1">You'll be the School Admin. Add teammates later from Settings.</p>

                            <form onSubmit={submit} className="mt-6 space-y-5 bg-white border border-border rounded-xl p-6" data-testid="signup-form">
                                <div className="text-xs font-semibold text-brand-emerald uppercase tracking-wide">School</div>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <F k="school_name" label="School name" req placeholder="Riverside Prep" />
                                    <F k="school_route" label="Veracross school route" req placeholder="riverside" className="mt-1 font-mono" />
                                    <F k="phone" label="Phone" placeholder="(555) 123-4567" />
                                    <F k="contact_email" label="Business office email" type="email" placeholder="business@school.edu" />
                                    <div className="sm:col-span-2"><F k="address_line1" label="Address line 1" /></div>
                                    <div className="sm:col-span-2"><F k="address_line2" label="Address line 2" /></div>
                                    <F k="city" label="City" />
                                    <div className="grid grid-cols-2 gap-4">
                                        <F k="state" label="State" maxLength={2} placeholder="TX" />
                                        <F k="zip_code" label="ZIP" placeholder="73301" />
                                    </div>
                                </div>

                                <div className="text-xs font-semibold text-brand-emerald uppercase tracking-wide pt-2">School Admin login</div>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <F k="admin_name" label="Your name" req placeholder="Jordan Lee" />
                                    <F k="admin_email" label="Your email" req type="email" placeholder="you@school.edu" />
                                    <div className="sm:col-span-2">
                                        <Label className="text-xs">Password<span className="text-rose-500"> *</span> <span className="text-muted-foreground">(min 8 characters)</span></Label>
                                        <Input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} className="mt-1" data-testid="signup-password" />
                                    </div>
                                </div>

                                <Button type="submit" disabled={busy} className="w-full bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid="signup-submit">
                                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account & start trial"}
                                </Button>
                            </form>

                            <p className="mt-5 text-sm text-muted-foreground">
                                Already have an account? <Link to="/login" className="text-brand-teal hover:underline" data-testid="to-login-link">Sign in</Link>
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
